/**
 * TodaysFocus - AI-powered task recommendations for today
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  dashboardApi,
  type TodaysFocusData,
  type FocusTask,
  type MeetingPrepItem,
  type BlockedTaskItem,
  isApiError,
} from '@services/api';

interface TodaysFocusProps {
  organizationId: string;
}

const priorityColors: Record<string, string> = {
  urgent: 'bg-red-50 dark:bg-red-900/20',
  high: 'bg-orange-50 dark:bg-orange-900/20',
  medium: 'bg-yellow-50 dark:bg-yellow-900/20',
  low: 'bg-green-50 dark:bg-green-900/20',
  none: 'bg-neutral-50 dark:bg-neutral-800',
};

function FocusScoreBadge({ score }: { score: number }) {
  const { t } = useTranslation();
  const percentage = Math.round(score * 100);
  const color = score >= 0.7 ? 'text-red-600 dark:text-red-400' : score >= 0.4 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400';
  return (
    <span className={`text-xs font-medium ${color}`}>
      {t('dashboard.priorityScore', { score: percentage })}
    </span>
  );
}

function FocusTaskCard({ task }: { task: FocusTask }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const priorityClass = priorityColors[task.priority] ?? priorityColors.none;

  return (
    <div
      className={`p-4 rounded-lg ${priorityClass} hover:shadow-md transition-shadow cursor-pointer`}
      onClick={() => navigate(`/boards/${task.boardId}?task=${task.taskId}`)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-neutral-900 dark:text-white">{task.title}</p>
          {task.projectName && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{task.projectName}</p>
          )}
        </div>
        <FocusScoreBadge score={task.focusScore} />
      </div>
      <p className="text-xs text-neutral-600 dark:text-neutral-300 mt-2 italic">{task.reason}</p>
      {task.dueDate && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
          {t('dashboard.due', { date: new Date(task.dueDate).toLocaleDateString() })}
        </p>
      )}
    </div>
  );
}

function MeetingPrepSection({ meetings }: { meetings: MeetingPrepItem[] }) {
  const { t } = useTranslation();
  if (!meetings || meetings.length === 0) return null;

  return (
    <div className="mt-4">
      <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 flex items-center gap-1.5">
        <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        {t('dashboard.meetingPrep')}
      </h4>
      <div className="space-y-2">
        {meetings.map((meeting) => (
          <div key={meeting.meetingId} className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
            <div className="flex items-center gap-2">
              {meeting.startTime && (
                <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">{meeting.startTime}</span>
              )}
              <span className="text-sm text-neutral-900 dark:text-white">{meeting.title}</span>
            </div>
            {meeting.prepNotes && (
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">{meeting.prepNotes}</p>
            )}
            {meeting.relatedTasks.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {meeting.relatedTasks.map((task, i) => (
                  <span key={i} className="text-xs bg-blue-100 dark:bg-blue-800/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                    {task}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BlockedTasksSection({ tasks }: { tasks: BlockedTaskItem[] }) {
  const { t } = useTranslation();
  if (!tasks || tasks.length === 0) return null;

  return (
    <div className="mt-4">
      <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 flex items-center gap-1.5">
        <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        {t('dashboard.blockedTasks')}
      </h4>
      <div className="space-y-2">
        {tasks.map((task) => (
          <div key={task.taskId} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
            <p className="text-sm text-neutral-900 dark:text-white">{task.title}</p>
            <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
              {t('dashboard.blockedBy', { blocker: task.blockedBy })}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TodaysFocus({ organizationId }: TodaysFocusProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<TodaysFocusData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<string | null>(null);

  const handleSendNow = async () => {
    try {
      setIsSending(true);
      setSendStatus(null);
      await dashboardApi.sendTodaysFocusNow(organizationId);
      setSendStatus(t('dashboard.briefingQueued'));
    } catch {
      setSendStatus(t('dashboard.briefingFailed'));
    } finally {
      setIsSending(false);
      setTimeout(() => setSendStatus(null), 3000);
    }
  };

  useEffect(() => {
    async function fetchFocus() {
      try {
        setIsLoading(true);
        setError(null);
        const result = await dashboardApi.getTodaysFocus(organizationId);
        setData(result);
      } catch (err) {
        if (isApiError(err)) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError(t('dashboard.failedLoadFocus'));
        }
      } finally {
        setIsLoading(false);
      }
    }

    if (organizationId) {
      void fetchFocus();
    }
  }, [organizationId, t]);

  const FocusHeader = () => (
    <div className="flex items-center gap-2 mb-4">
      <svg
        className="w-5 h-5 text-primary-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      </svg>
      <h3 className="text-lg font-medium text-neutral-900 dark:text-white">{t('dashboard.todaysFocus')}</h3>
    </div>
  );

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <svg
            className="w-5 h-5 text-primary-500 animate-pulse"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white">{t('dashboard.todaysFocus')}</h3>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-full" />
          <div className="h-16 bg-neutral-100 dark:bg-neutral-700 rounded" />
          <div className="h-16 bg-neutral-100 dark:bg-neutral-700 rounded" />
          <div className="h-16 bg-neutral-100 dark:bg-neutral-700 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
        <FocusHeader />
        <div className="text-center py-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            {t('dashboard.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (!data || data.recommendedTasks.length === 0) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
        <FocusHeader />
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-neutral-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
            />
          </svg>
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{t('dashboard.noRecommendations')}</p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500">{t('dashboard.startAddingTasks')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
      <div className="flex items-center gap-2 mb-2">
        <svg
          className="w-5 h-5 text-primary-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
        <h3 className="text-lg font-medium text-neutral-900 dark:text-white">{t('dashboard.todaysFocus')}</h3>
        <span className="ml-auto text-xs text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 px-2 py-0.5 rounded-full">
          {t('dashboard.aiPowered')}
        </span>
      </div>

      <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">{data.summary}</p>

      {data.meetingContext && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-4">
          <p className="text-xs text-blue-700 dark:text-blue-300">{data.meetingContext}</p>
        </div>
      )}

      <div className="space-y-3">
        {data.recommendedTasks.map((task) => (
          <FocusTaskCard key={task.taskId} task={task} />
        ))}
      </div>

      <MeetingPrepSection meetings={data.meetingPrep} />
      <BlockedTasksSection tasks={data.blockedTasks} />

      {data.productivityTip && (
        <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {t('dashboard.tip', { tip: data.productivityTip })}
          </p>
        </div>
      )}

      {data.estimatedCompletionTime && (
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-2">
          {t('dashboard.estimatedTime', { time: data.estimatedCompletionTime })}
        </p>
      )}

      <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
        <button
          onClick={handleSendNow}
          disabled={isSending}
          className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 disabled:opacity-50 flex items-center gap-1"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          {isSending ? t('dashboard.sendingFocus') : t('dashboard.emailMeFocus')}
        </button>
        {sendStatus && (
          <span className="text-xs text-neutral-500 dark:text-neutral-400">{sendStatus}</span>
        )}
      </div>
    </div>
  );
}
